import { ChangeDetectorRef, inject, Injectable, InjectionToken, OnDestroy } from '@angular/core';

import { VirtualScrollerComponent } from '@iharbeck/ngx-virtual-scroller';
import { BehaviorSubject, Subject } from 'rxjs';
import { debounceTime, map, startWith, switchMap, takeUntil } from 'rxjs/operators';

import { ItemSelector, SelectItemStrategy } from './default-strategies/select-item-strategy';
import { Select2ListItemDirective } from './default-templates/list-item/list-item.directive';
import { Select2SelectedItemDirective } from './default-templates/selected-item/selected-item.directive';
import { Select2TriggerDirective } from './default-templates/trigger/trigger.directive';
import { HeaderTemplate, Select2Item, SelectFilterFn } from './types';

export const SELECT_DATASOURCE = new InjectionToken<BehaviorSubject<Select2Item[]>>(
  'Select data source',
);

@Injectable()
export class SelectController<T = any> implements OnDestroy {
  public filtersMap: Record<string, SelectFilterFn> = {};
  public headerTemplates: HeaderTemplate[] = [];
  public itemSelector = inject(ItemSelector, { optional: true }) ?? new SelectItemStrategy();

  public isOpen$ = new BehaviorSubject<boolean>(false);
  public clear$ = new Subject<void>();
  public items$ = new BehaviorSubject<Select2Item[]>([]);
  public itemsToRender$ = new BehaviorSubject<Select2Item[]>([]);
  public select$ = new Subject<Select2Item | Select2Item[]>();
  public keyDown$ = new Subject<KeyboardEvent>();
  public navigation$ = new BehaviorSubject<{ type: 'item'; index: number }>({
    type: 'item',
    index: 0,
  });
  public focusTrigger$ = new Subject<void>();
  public itemsRefresh$ = new Subject<void>();
  public virtualScroll$ = new BehaviorSubject<VirtualScrollerComponent | null>(null);

  public triggerDirective: Select2TriggerDirective;
  public listItemDirective: Select2ListItemDirective<T>;
  public selectedItemDirective: Select2SelectedItemDirective<T>;

  private ngOnDestroy$ = new Subject<void>();

  constructor(public cdRef: ChangeDetectorRef) {
    const dataSource$ =
      inject(SELECT_DATASOURCE, { optional: true }) ?? this.getDefaultDataSource();
    dataSource$
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe(items => this.itemsToRender$.next(items));
  }

  public setVirtualScroll(component: VirtualScrollerComponent | undefined) {
    this.virtualScroll$.next(component ?? null);
  }

  public ngOnDestroy() {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public toggle(value?: boolean) {
    const nextValue = value ?? !this.isOpen$.value;
    this.isOpen$.next(nextValue);
  }

  public filterRemove(name: string) {
    const { [name]: _, ...filtersMap } = this.filtersMap;
    this.filtersMap = filtersMap;
  }

  public filterRegister(name: string, fn: SelectFilterFn) {
    this.filtersMap = {
      ...this.filtersMap,
      [name]: fn,
    };
  }

  public filterItems(items: Select2Item[]): Select2Item[] {
    const filters = Object.values(this.filtersMap);
    return items.filter(item => filters.every(filter => filter(item)));
  }

  public itemsRefresh() {
    this.itemsRefresh$.next();
  }

  public scrollToIndex(index: number) {
    if (!this.virtualScroll$.value) return;

    this.virtualScroll$.value.scrollToIndex(index, false, 100);
  }

  public headerTemplateRegister(template: HeaderTemplate) {
    this.headerTemplates = [...this.headerTemplates, template].sort((a, b) =>
      a.index > b.index ? 1 : -1,
    );
  }

  public headerTemplateRemove(name: string) {
    this.headerTemplates = this.headerTemplates.filter(template => template.name !== name);
  }

  private getDefaultDataSource() {
    return this.itemsRefresh$.pipe(
      startWith(null),
      switchMap(() =>
        this.items$.pipe(
          debounceTime(0),
          map(items => this.filterItems(items)),
        ),
      ),
    );
  }
}
